import { useEffect, useRef, useState } from "react";

function collectRecognitionUpdate(event) {
  const results = event?.results;

  if (!results || typeof results.length !== "number") {
    return { finalChunk: "", interimText: "" };
  }

  const startIndex =
    Number.isInteger(event?.resultIndex) && event.resultIndex >= 0
      ? event.resultIndex
      : 0;

  const finalSegments = [];
  const interimSegments = [];

  for (let index = startIndex; index < results.length; index += 1) {
    const result = results[index];
    const transcript = String(result?.[0]?.transcript || "").trim();

    if (!transcript) {
      continue;
    }

    if (result?.isFinal) {
      finalSegments.push(transcript);
    } else {
      interimSegments.push(transcript);
    }
  }

  return {
    finalChunk: finalSegments.join(" ").trim(),
    interimText: interimSegments.join(" ").trim()
  };
}

function useSpeechRecognition(language = "en-US") {
  const recognitionRef = useRef(null);
  const finalTextRef = useRef("");
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [interimText, setInterimText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const BrowserSpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!BrowserSpeechRecognition) {
      setSupported(false);
      return undefined;
    }

    setSupported(true);

    const recognition = new BrowserSpeechRecognition();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setListening(true);
      setError("");
    };

    recognition.onerror = (event) => {
      setError(event?.error || "Speech recognition error");
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.onresult = (event) => {
      const { finalChunk: nextFinalChunk, interimText: nextInterimText } =
        collectRecognitionUpdate(event);

      if (nextFinalChunk) {
        setFinalText((previousText) => {
          const combinedText = `${previousText} ${nextFinalChunk}`.trim();
          finalTextRef.current = combinedText;
          return combinedText;
        });
      }

      setInterimText(nextInterimText);
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch (error) {
        // Ignore cleanup stop issues.
      }

      recognitionRef.current = null;
    };
  }, [language]);

  function startListening() {
    setError("");

    try {
      recognitionRef.current?.start();
      return true;
    } catch (nextError) {
      setError("Speech capture could not be started.");
      return false;
    }
  }

  function stopListening() {
    try {
      recognitionRef.current?.stop();
    } catch (error) {
      // Ignore repeated stop calls.
    }
  }

  function resetTranscript() {
    finalTextRef.current = "";
    setFinalText("");
    setInterimText("");
    setError("");
  }

  return {
    supported,
    listening,
    finalText,
    interimText,
    error,
    startListening,
    stopListening,
    resetTranscript
  };
}

export { useSpeechRecognition };

export default useSpeechRecognition;
